import React from 'react';
import { useDispatch } from 'react-redux';
import ReactCrop, { type Crop } from 'react-image-crop';
import { Button, Divider, Grid, IconButton, DialogActions, DialogContent, DialogTitle, Tooltip } from '@mui/material';

import { openDialog, closeDialog } from 'app/store/fuse/dialogSlice';
import useSettings from '../hooks/useSettings';
import { executeApi } from '../Utils';
import { getFileExtension } from '../files-explorer/FilesUtils';
import { AspectRatios, exportImage, rotateImage } from './ImageUtils';
import Icon from '../Icon';

import 'react-image-crop/dist/ReactCrop.css';
import ImageEditorResizeDialog from './ImageEditorResizeDialog';

interface ImageEditorDialogProps {
	/** The field name */
	name: string;
	/** The image path */
	path: string;
	/** The image name */
	value: string;
	/** The image content */
	uploadedImage?: string;
	/** The image content setter function */
	setUploadedImage: (newImage: string | null) => void;
	/** The sendChange function (from useModel) */
	sendChange: (name: string, path: string, editedImage: File) => void;
	/** The image ratio property, e.g. 1:1, 4:3 or 16:9, etc. */
	aspect?: AspectRatios;
}

export const showImageEditorDialog = (props: ImageEditorDialogProps) =>
	openDialog({
		children: <ImageEditorDialog {...props} />,
		fullWidth: true,
		maxWidth: 'sm',
		onClose: () => {}
	});

export default function ImageEditorDialog({
	name,
	path,
	value,
	uploadedImage,
	setUploadedImage,
	sendChange,
	aspect = AspectRatios['1:1']
}: ImageEditorDialogProps) {
	const dispatch = useDispatch();
	const { sysSettings, t } = useSettings();

	const [filesRootPath] = React.useState(sysSettings.files_path);

	const imgRef = React.useRef<HTMLImageElement | null>(null);
	const [crop, setCrop] = React.useState<Partial<Crop>>({ aspect });
	const [image, setImage] = React.useState<string | null>(null);
	const [imageDimensions, setImageDimensions] = React.useState<{ width: number; height: number }>();

	// Resize Dialog
	const [openResizeDialog, setOpenResizeDialog] = React.useState(false);
	const [resizeDialogAnchor, setResizeDialogAnchor] = React.useState<HTMLButtonElement>();

	const switchOpenResizeDialog = () => setOpenResizeDialog(val => !val);

	const saveImage = async () => {
		if (imgRef.current !== null) {
			const img = await exportImage(imgRef.current, crop, value);

			if (img !== null) {
				const file = new File([img], value, { type: img.type });
				sendChange(name, `${sysSettings.files_path}\\${path}`, file);

				const reader = new FileReader();
				reader.addEventListener('load', () => {
					setUploadedImage(reader.result as string);
				});
				reader.readAsDataURL(file);

				dispatch(closeDialog());
			}
		}
	};

	const performImageRotation = async () => {
		if (image !== null) {
			const img = await rotateImage(image, 90, value);

			if (img !== null) {
				const file = new File([img], value, { type: img.type });

				const reader = new FileReader();
				reader.addEventListener('load', () => setImage(reader.result as string));
				reader.readAsDataURL(file);
			}
		}
	};

	const onLoad = (img: HTMLImageElement) => {
		setImageDimensions({ width: img.width, height: img.height });
		imgRef.current = img;
	};

	React.useEffect(() => {
		if (uploadedImage) {
			setImage(uploadedImage);
		} else if (!uploadedImage && value) {
			const filePath = `${filesRootPath}${path}\\${value}`;

			executeApi.get({
				url: `/api/filexchange/file?path=${filePath}`,
				dispatch,
				successHandler: response => {
					const extension = getFileExtension(value);
					setImage(`data:image/${extension};base64,${response.data}`);
				}
			});
		}
	}, [dispatch, filesRootPath, path, uploadedImage, value]);

	return (
		<>
			<DialogTitle>{t('DIALOG_IMAGE_EDITOR')}</DialogTitle>
			<DialogContent>
				<Grid container spacing={1}>
					<Grid size={12}>
						<Tooltip title="Resize">
							<IconButton onClick={switchOpenResizeDialog} onFocus={e => setResizeDialogAnchor(e.target)}>
								<Icon icon="resize" />
							</IconButton>
						</Tooltip>
						<IconButton onClick={performImageRotation} disabled>
							<Icon icon="arrow-left-bottom" />
						</IconButton>
					</Grid>
					<Grid size={12}>
						<Divider />
					</Grid>
					<Grid size={12}>
						{image && (
							<ReactCrop ruleOfThirds crop={crop} src={image} onImageLoaded={onLoad} onChange={setCrop} />
						)}
					</Grid>
				</Grid>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => dispatch(closeDialog())}>{t('DIALOG_ACTION_CANCEL')}</Button>
				<Button onClick={saveImage}>{t('DIALOG_ACTION_CONFIRM')}</Button>
			</DialogActions>
			<ImageEditorResizeDialog
				anchorElement={resizeDialogAnchor}
				open={openResizeDialog}
				handleClose={switchOpenResizeDialog}
				name={value}
				image={image}
				setImage={setImage}
				imageDimensions={imageDimensions}
			/>
		</>
	);
}
